import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Chip from '@material-ui/core/Chip';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import relationships from 'Consts/Relationships';
import useCollection from 'Hooks/useCollection';
import useResource from 'Hooks/useResource';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useUserContext from 'Context/UserContext';
import MetaControl from 'Components/UI/Meta/MetaControl';
import WorkspacesOverride from 'Overrides/Workspaces';
import RoundButton from '../../../../UI/RoundButton';
import { displayErrorMessage, displaySuccess } from '../../../../../Helpers/FacetNotificationManager';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing(0, 0.5, 0.5, 0),
  },
});

const Workspaces = ({
  className,
  classes,
}) => {
  const { userResource } = useUserContext();
  const { collection, setCollection } = useCollection();
  const { resource: workspaceResource, initializeResource, setAttribute } = useResource();
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const {
    rootNodeProps,
    logResourceEvent,
    logResourceEventSuccess,
    logResourceEventFailureWithError,
  } = useEventTrackingLogEvent({
    nodeName: 'Workspaces',
    isPageView: true,
  });

  const handleUpdateTabs = async (selectedTabs) => {
    setAttribute('tabs', selectedTabs.filter(tab => tab !== ''));
  };

  const handleUpdateWorkspace = async () => {
    const eventName = 'Clicked Save Workspace';

    logResourceEvent(eventName, workspaceResource);
    try {
      setIsSaveDisabled(true);
      const workspaceCollection = await workspaceResource.submit();
      initializeResource(workspaceCollection.resources().head());
      displaySuccess('Workspace settings saved');
      logResourceEventSuccess(eventName, workspaceResource);
    } catch (err) {
      logResourceEventFailureWithError(eventName, workspaceResource, err);
      displayErrorMessage(err, 'Failed to save workspace settings');
    } finally {
      setIsSaveDisabled(false);
    }
  };

  useEffect(() => {
    if (userResource) {
      userResource.relationship(relationships.workspaces)
        .link('related')
        .Get({ metaOverrideSet: WorkspacesOverride })
        .then(setCollection);
    }
  }, [userResource]);

  useEffect(() => {
    if (collection) {
      if (!collection.resources().head()) {
        collection.create({
          addToCollection: true,
          metaOverrideSet: WorkspacesOverride,
        });
      }
      initializeResource(collection.resources().head());
    }
  }, [collection]);

  return (
    <div
      className={classNames(classes.root, className)}
      {...rootNodeProps}
    >
      <div className={classes.description}>
        Select which pages you would like to have opened when you launch your Workspace.
      </div>
      {workspaceResource && (
        <>
          <MetaControl
            attrName="tabs"
            resource={workspaceResource}
            onChange={handleUpdateTabs}
            fullWidth
            SelectProps={{
              renderValue: selected => (
                <div className={classes.chips}>
                  {selected.map((value) => {
                    const label = workspaceResource
                      .meta()
                      .field('tabs')
                      .option(value)
                      .attr('prompt');
                    return (
                      <Chip
                        key={value}
                        label={label}
                        className={classes.chip}
                      />
                    );
                  })}
                </div>
              ),
            }}
          />
          <RoundButton
            color="secondary"
            variant="contained"
            onClick={handleUpdateWorkspace}
            disabled={isSaveDisabled}
          >
              Save
          </RoundButton>
        </>
      )}
    </div>
  );
};

Workspaces.defaultProps = {
  className: '',
};

Workspaces.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Workspaces);
